import { Component, OnDestroy, OnInit } from '@angular/core';
import { debounceTime, forkJoin, map, Observable, Subject, Subscription, switchMap, tap } from 'rxjs';
import { ChipsAutocompleteElem, ConfirmationDialogComponent, InfoDialogComponent }  from '@trivelope/triv-ui-tools-lib';
import { DocumentExchange, DocumentExchangeCategory } from 'src/app/classes/document-exchange';
import { DocumentExchangeService } from 'src/app/services/document-exchange.service';
import { Member } from 'src/app/classes/member';
import { MemberService } from 'src/app/services/member.service';
import { AuthenticationService } from 'src/app/services/authentication.service';
import { FormControl, FormGroup } from '@angular/forms';
import { PageEvent } from '@angular/material/paginator';
import { MatDialog } from '@angular/material/dialog';
import { FileService } from 'src/app/services/file.service';
import { ActivatedRoute } from '@angular/router';
import { NewCountersService } from 'src/app/services/new-counters.service';


@Component({
	selector: 'document-exchange-list',
	templateUrl: './document-exchange-list.component.html',
	styleUrls: [ './document-exchange-list.component.css' ],
})
export class DocumentExchangeListComponent implements OnInit, OnDestroy {


	bFirstLoading = true;
	bLoadingError = false;
	bNoCategories = false;
	bInProgress = false;
	bDeleting: { [key:string]: boolean } = {};
	bTogglingViewed: { [key:string]: boolean } = {};
	subscriptions = new Subscription();
	searchSubject = new Subject<void>();
	bSearching = false;

	documentExchanges: DocumentExchange[] = [];
	documentExchangeCategories: DocumentExchangeCategory[] = [];
	documentExchangeCategoryNames: { [key: string]: string } = {};
	categoryChips: ChipsAutocompleteElem[] = [];
	selectedCategories: string[] = [];
	members: Member[] = [];
	membersCombo: { id: string, label: string }[] = [];
	memberNames: { [key: string]: string } = {};
	selectedMember = "";

	filterFormGroup = new FormGroup( {
		member: new FormControl<string>( "" ),
		category: new FormControl<string[]>( [] ),
		dateStart: new FormControl<moment.Moment|null>( null ),
		dateEnd: new FormControl<moment.Moment|null>( null ),
		description: new FormControl( "" ),
		incomingOutgoing: new FormControl<"in"|"out"|"all">( "in" )
	} );

	totalNb = 0;
	currentPage = 0;

	bAdmin = false;
	submenuCategories: DocumentExchangeCategory[] = [];


	constructor(
		private documentExchangeService: DocumentExchangeService,
		private memberService: MemberService,
		private authenticationService: AuthenticationService,
		private matDialog: MatDialog,
		private fileService: FileService,
		private route: ActivatedRoute,
		private newCountersService: NewCountersService
	) {}


	ngOnInit() {
		this.bAdmin = this.authenticationService.isAdmin();

		const obss: Observable<boolean>[] = [];

		// Kategorien laden
		obss.push(this.documentExchangeService.getDocumentExchangeCategories().pipe(
			map(categories => {
				if (categories === null) {
					this.bLoadingError = true;
					return false;
				} else if (categories.length === 0) {
					this.bNoCategories = true;
					return true;
				} else {
					this.documentExchangeCategories = categories;
					for (const category of categories) {
						this.documentExchangeCategoryNames[category.id] = category.name;
						this.categoryChips.push({ id: category.id, label: category.name });
					}
					return true;
				}
			})
		));

		if (this.bAdmin) {
			obss.push(this.memberService.getAll().pipe(
				map(members => {
					if (members === null) {
						this.bLoadingError = true;
						return false;
					} else {
						this.members = members;
						for (const member of members) {
							this.memberNames[member.mitgliedId] = member.mitgliedsname;
							this.membersCombo.push({ id: member.mitgliedId, label: member.mitgliedsname });
						}
						return true;
					}
				})
			));
		}

		// Merken, was aus queryParams kommt (aber noch nicht anwenden)
		let initialTypFilter: string | null = null;
		this.route.queryParams.subscribe(params => {
			initialTypFilter = params['typ'] || null;
		});

		// Erst nachdem alles geladen ist
		forkJoin(obss).subscribe(results => {
			this.bFirstLoading = false;

			if (!this.bLoadingError) {
				if (initialTypFilter) {
					this.selectedCategories = [initialTypFilter];
					this.filterFormGroup.patchValue({ category: [initialTypFilter] });
					this.onNewCategoryFilter(this.selectedCategories);
				}
				this.searchSubject.next();
			} else {
				this.bSearching = false;
			}
		});

		// Nur EIN Such-Subscription
		this.subscriptions.add(
			this.searchSubject.pipe(
				debounceTime(300),
				switchMap(() => this.filter())
			).subscribe()
		);

		this.subscriptions.add(
			this.filterFormGroup.valueChanges.pipe(
				tap(() => this.searchSubject.next())
			).subscribe()
		);
	}


	private initializeFiltersFromQueryParams() {
		this.route.queryParams.subscribe(params => {
			const typ = params['typ'];
			if (typ) {
				this.selectedCategories = [typ];
				this.filterFormGroup.patchValue({ category: [typ] });
				this.onNewCategoryFilter(this.selectedCategories);
			}
		});
	}

	ngOnDestroy() {
		this.subscriptions.unsubscribe();
	}



	onNewCategoryFilter( ids: string[] ) {

		console.log( "onNewMemberFilter", ids )
		this.selectedCategories = ids;
		this.searchSubject.next();

	}

	onMemberSelected( id: string | null ) {

		console.log( "onMemberSelected", id )
		if( id === null ) {
			this.selectedMember = "";
		}
		else {
			this.selectedMember = id;
		}

		this.searchSubject.next();

	}

	private filter(): Observable<boolean> {

		console.log("START FILTER");


		const startDt = this.filterFormGroup.get( "dateStart" )?.value || null;
		const endDt = this.filterFormGroup.get( "dateEnd" )?.value || null;
		const inbox = this.filterFormGroup.get( "incomingOutgoing" )?.value || "all";
		let bInbox = null;
		if( inbox == "in" ) bInbox = true;
		else if( inbox == "out" ) bInbox = false;

		this.bSearching = true;
		return this.documentExchangeService.getDocumentExchanges(
			this.currentPage,
			this.selectedMember ? [ this.selectedMember ] : null,
			this.selectedCategories.length ? this.selectedCategories : null,
			null,
			startDt ? startDt.valueOf() : null,
			endDt ? endDt.valueOf() : null,
			this.filterFormGroup.get( "description" )?.value || "",
			bInbox
		).pipe(
			map( response => {

				console.log( "FILTER", response )
				this.bFirstLoading = false;
				this.bSearching = false;

				if (response === null) {
				this.bLoadingError = true;
				return false;
			} else {
				this.bLoadingError = false;

				if (!response.elements.length && response.total) {
					this.bSearching = true;
					this.currentPage = 0;
					this.searchSubject.next();
					return true;
				} else {
					this.documentExchanges = response.elements;
					this.totalNb = response.total;
					return true;
				}
			}
			} )
		);

	}

	handlePageEvent( e: PageEvent ) {

		this.currentPage = e.pageIndex;
		this.searchSubject.next();

	}

	clickDelete( de: DocumentExchange ) {

		this.matDialog.open(
			ConfirmationDialogComponent,
			{ data:{ message: "Das Löschen dieses Dokuments kann nicht rückgängig gemacht werden. Möchten Sie fortfahren?" } }
		).afterClosed().subscribe( bConfirm => {

			if( !bConfirm ) return;

			this.bDeleting[ de.id ] = true;
			this.bInProgress = true;
			this.documentExchangeService.deleteDocumentExchange( de.id ).subscribe( b => {

				this.bDeleting[ de.id ] = false;
				this.bInProgress = false;

				if( b ) {
					const index = this.documentExchanges.findIndex( d => d.id == de.id );
					if( index != -1 ) {
						this.documentExchanges.splice( index, 1 );
					}
					this.searchSubject.next();
				}
				else {
					this.matDialog.open( InfoDialogComponent, {
						data: { title: "Fehler", message: "Beim Löschen des Dokuments ist ein Fehler aufgetreten" }
					});
				}

			});

		});

	}

	clickSwitchViewedStatus( de: DocumentExchange, bNew: boolean ) {

		this.bTogglingViewed[ de.id ] = true;
		this.bInProgress = true;

		this.documentExchangeService.setViewedStatus( de.id, bNew ).subscribe( bResult => {

			this.bTogglingViewed[ de.id ] = false;
			this.bInProgress = false;

			if( bResult ) {
				de.bSeen = bNew;
			}
			else {
				this.matDialog.open( InfoDialogComponent, {
					data: { title: "Fehler", message: "Beim Ändern des angezeigten Status ist ein Fehler aufgetreten" }
				});
			}

		});

	}

	clickToggleViewedStatus( de: DocumentExchange ) {

		if( !de.bOwn ) return;
		if( this.bTogglingViewed[ de.id ] ) return;

		this.clickSwitchViewedStatus( de, !de.bSeen );

	}


	downloadFile( file: { id: string, filename: string }, documentExchange: DocumentExchange ) {

		this.fileService.downloadFileByIdObs( file.id, file.filename ).subscribe( b => {

			if( b ) {
				if( !documentExchange.bSeen ) {
					this.newCountersService.decrementNewDocuments();
				}
				documentExchange.bSeen = true;
			}

		});

	}

	/*
	clickSort( field: string ) {

		this.sortingAsc = !this.sortingAsc;
		this.sortingField = field;
		console.log( "clickSort", field, this.sortingAsc )

		// this.searchSubject.next();

	}
	*/

}



